import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, DASHBOARD_ENTERED_EVENT, HOME_PAGE_EVENT, ONBOARDING_EVENT } from '../analytics/posthog';
import styles from './Home.module.css';
import config from '../config';
import BrandLogo from './BrandLogo';
import logoImage from '../assets/logo.png';
import hillImage from '../assets/hill.png';
import { getOnboardingAIData, getToken, handleApiError } from '../utils/api';
import { useContentCatalog } from '../utils/contentCatalog';
import { canEnterV2OnboardingAgent, matchesSignupCollectedName } from '../utils/helper';

const PARALLAX_ARM_MS = 1100;
const PARALLAX_SHIFT_X = 14;
const PARALLAX_SHIFT_Y = 10;
const PARALLAX_BADGE_FACTOR = 0.35;
const PARALLAX_IMAGE_SCALE = 1.07;

const WAVE_DESKTOP = {
  viewBox: '0 0 240 1000',
  d: 'M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z',
  dur: '14s',
  values:
    'M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z;M0 0 H150 C150 90 58 168 70 310 C84 452 196 518 186 658 C176 798 86 862 112 1000 H0 Z;M0 0 H168 C168 70 38 150 48 290 C58 430 210 490 198 640 C186 800 72 870 96 1000 H0 Z',
};

const WAVE_SHEET = {
  viewBox: '0 0 576 1024',
  d: 'M 0 210 C 6 211.7, 24 215.3, 36 220 C 48 224.7, 60 229.3, 72 238 C 84 246.7, 96 259.3, 108 272 C 120 284.7, 132 303.3, 144 314 C 156 324.7, 168 330.7, 180 336 C 192 341.3, 202 343.3, 216 346 C 230 348.7, 248 349.7, 264 352 C 280 354.3, 296 356.5, 312 360 C 328 363.5, 344 368.3, 360 373 C 376 377.7, 392 381.2, 408 388 C 424 394.8, 440 403.3, 456 414 C 472 424.7, 490 442, 504 452 C 518 462, 528 467.3, 540 474 C 552 480.7, 570 489, 576 492 L 576 1024 L 0 1024 Z',
};

const SPARKLES = ['sparkleA', 'sparkleB', 'sparkleC'];

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function useHeroParallax(enabled) {
  const heroRef = useRef(null);
  const imageRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const hero = heroRef.current;
    const img = imageRef.current;
    const badge = badgeRef.current;
    if (!hero || !img) {
      return undefined;
    }

    let armed = false;
    let raf = 0;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, PARALLAX_ARM_MS);

    const reset = () => {
      img.style.transform = '';
      if (badge) {
        badge.style.transform = '';
      }
    };

    const onMove = (event) => {
      if (!armed) {
        return;
      }
      const bounds = hero.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) {
        return;
      }
      const nx = (event.clientX - bounds.left) / bounds.width - 0.5;
      const ny = (event.clientY - bounds.top) / bounds.height - 0.5;
      cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(() => {
        const tx = nx * PARALLAX_SHIFT_X;
        const ty = ny * PARALLAX_SHIFT_Y;
        img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${PARALLAX_IMAGE_SCALE})`;
        if (badge) {
          badge.style.transform = `translate3d(${-tx * PARALLAX_BADGE_FACTOR}px, ${-ty * PARALLAX_BADGE_FACTOR}px, 0)`;
        }
      });
    };

    hero.addEventListener('pointermove', onMove, { passive: true });
    hero.addEventListener('pointerleave', reset);
    hero.addEventListener('pointercancel', reset);

    return () => {
      window.clearTimeout(armTimer);
      cancelAnimationFrame(raf);
      hero.removeEventListener('pointermove', onMove);
      hero.removeEventListener('pointerleave', reset);
      hero.removeEventListener('pointercancel', reset);
      reset();
    };
  }, [enabled]);

  return { heroRef, imageRef, badgeRef };
}

function resolveContinueDestination(userData) {
  if (userData.onboarding_ai_done) {
    return { path: '/dashboard/tracks/your-quick-richuels', kind: 'dashboard' };
  }

  const hasExistingProgress =
    Array.isArray(userData?.v2_onboarding_ai?.chat) && userData.v2_onboarding_ai.chat.length > 0;

  if (canEnterV2OnboardingAgent(userData)) {
    return {
      path: '/v2-onboarding-agent',
      kind: 'onboarding',
      hasExistingProgress,
    };
  }

  return {
    path: '/onboarding-initial',
    kind: 'onboarding',
    hasExistingProgress: false,
  };
}

const BriefcaseIcon = () => (
  <svg className={`${styles.featureSvg} ${styles.briefcaseIcon}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M8 7V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1M4 11h16M4 10.5A2.5 2.5 0 0 1 6.5 8h11A2.5 2.5 0 0 1 20 10.5v7A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-7Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className={`${styles.featureSvg} ${styles.calendarIcon}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="4.25" y="5.25" width="15.5" height="14.5" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 3.6v3.2M16 3.6v3.2M4.25 10.1h15.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const ShieldIcon = () => (
  <svg className={`${styles.featureSvg} ${styles.shieldIcon}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 3l7 4v6c0 5-3.5 8.5-7 9.5-3.5-1-7-4.5-7-9.5V7l7-4z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path d="M9.2 12.2l1.9 1.9 3.8-4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg className={styles.betaLock} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="5.5" y="11" width="13" height="9.5" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8.2 11V8.4a3.8 3.8 0 0 1 7.6 0V11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const HOME_FEATURE_ART = {
  planning: { revealClass: 'featureRevealA', wellClass: 'wellPlan', Icon: BriefcaseIcon },
  security: { revealClass: 'featureRevealB', wellClass: 'wellSecure', Icon: ShieldIcon },
};

function DecorativeWave({ className, wave, motionEnhanced }) {
  return (
    <svg className={className} viewBox={wave.viewBox} preserveAspectRatio="none" aria-hidden focusable="false">
      <path d={wave.d}>
        {motionEnhanced ? (
          <animate attributeName="d" dur={wave.dur} repeatCount="indefinite" values={wave.values} />
        ) : null}
      </path>
    </svg>
  );
}

const Home = () => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const copy = catalog?.marketing?.home;
  const featurePills = useMemo(
    () => (copy?.features || []).map((feature) => ({
      ...feature,
      ...(HOME_FEATURE_ART[feature.id] || {}),
    })).filter((pill) => pill.Icon),
    [copy?.features],
  );
  const posthog = usePostHog();
  const motionEnhanced = !prefersReducedMotion();
  const { heroRef, imageRef, badgeRef } = useHeroParallax(motionEnhanced);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContinue = useCallback(async () => {
    capture(posthog, HOME_PAGE_EVENT, { page: 'home', action: 'start_journey' });

    const token = getToken();
    if (!token) {
      navigate('/signup');
      return;
    }

    try {
      const response = await getOnboardingAIData(token, navigate, false);
      if (!response?.success || !response?.data) {
        navigate('/onboarding-initial');
        return;
      }

      const userData = response.data;
      if (userData.name) {
        localStorage.setItem(config.STORAGE_KEYS.NAME, userData.name);
      }

      const destination = resolveContinueDestination(userData);
      const openAgentAfterCollectedName =
        destination.path === '/onboarding-initial' && matchesSignupCollectedName(userData.name);
      const nextPath = openAgentAfterCollectedName ? '/v2-onboarding-agent' : destination.path;
      if (destination.kind === 'dashboard') {
        capture(posthog, DASHBOARD_ENTERED_EVENT, { source: 'home', onboarding_complete: true });
      } else {
        capture(posthog, ONBOARDING_EVENT, {
          step: 'onboarding_continued',
          from: 'home',
          has_existing_progress: destination.hasExistingProgress,
        });
      }
      navigate(nextPath);
    } catch (error) {
      handleApiError(error, navigate);
    }
  }, [navigate, posthog]);

  return (
    <div className={`${styles.homePage} ${motionEnhanced ? styles.homeSmooth : ''}`}>
      <main className={styles.homeMain} id="home-main" aria-label="Home">
        <section ref={heroRef} className={styles.heroCard} aria-labelledby="home-headline">
          <div className={styles.copyCol}>
            <div className={styles.brand}>
              <BrandLogo src={logoImage} alt="Richuel" decoding="async" />
            </div>

            <div className={styles.copyBlock}>
              <h1 id="home-headline" className={styles.headline}>
                <span className={styles.headlineLine}>{copy?.headline_line_1}</span>
                <span className={styles.headlineLine}>{copy?.headline_line_2}</span>
              </h1>
              <p className={styles.subcopy}>{copy?.subcopy}</p>
              <button type="button" className={styles.cta} onClick={handleContinue} aria-label={copy?.continue_aria}>
                <span>{copy?.continue_label}</span>
                <span className={styles.ctaArrows} aria-hidden>
                  {'>>'}
                </span>
              </button>
            </div>

            <div className={styles.featurePills}>
              {featurePills.map(({ id, revealClass, wellClass, Icon, label }) => (
                <div key={id} className={`${styles.featurePill} ${styles[revealClass]}`}>
                  <span className={`${styles.featureIconWell} ${styles[wellClass]}`}>
                    <Icon />
                    {wellClass === 'wellPlan' ? <CalendarIcon /> : null}
                  </span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.visualCol}>
            <div className={styles.imageClip}>
              <img
                ref={imageRef}
                src={hillImage}
                alt=""
                className={styles.heroImg}
                decoding="async"
                fetchPriority="high"
              />
              <div className={styles.imageShimmer} aria-hidden />
              <div className={styles.sparkles} aria-hidden>
                {SPARKLES.map((sparkle) => (
                  <span key={sparkle} className={styles[sparkle]} />
                ))}
              </div>
            </div>

            <DecorativeWave className={styles.wave} wave={WAVE_DESKTOP} motionEnhanced={motionEnhanced} />
          </div>

          <DecorativeWave className={styles.sheet} wave={WAVE_SHEET} motionEnhanced={false} />

          <div ref={badgeRef} className={styles.betaBadge}>
            <span className={styles.betaIcon} aria-hidden>
              <LockIcon />
            </span>
            <p className={styles.betaText}>{copy?.beta_notice}</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
